import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Firebase } from '../../firebase/config';
import { serverTimestamp } from '../../firebase/collections/fieldValues';
import { ToastContext } from '../../contextStore/ToastContext';
import ConfirmModal from './ConfirmModal';
import './AdActions.css';

function AdActions({ product, isOwner, onSold, onFeaturedRequest }) {
  const { t } = useTranslation();
  const history = useHistory();
  const { addToast } = React.useContext(ToastContext) || {};
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(false);

  if (!isOwner || !product) return null;

  const handleDelete = () => {
    Firebase.firestore()
      .collection('products')
      .doc(product.id)
      .delete()
      .then(() => {
        setShowDeleteModal(false);
        history.push('/dashboard');
      });
  };

  const handleMarkSold = () => {
    Firebase.firestore()
      .collection('products')
      .doc(product.id)
      .update({
        status: 'sold',
        soldAt: serverTimestamp(),
      })
      .then(() => {
        setShowSoldModal(false);
        if (onSold) onSold();
      });
  };

  const handleRequestFeatured = () => {
    if (!product?.id) return;
    setFeaturedLoading(true);
    Firebase.firestore()
      .collection('products')
      .doc(product.id)
      .update({
        featuredRequestStatus: 'requested',
        featuredRequestedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      .then(() => {
        setFeaturedLoading(false);
        addToast?.(t('dashboard.featuredRequestSubmitted'), 'success');
        if (onFeaturedRequest) onFeaturedRequest();
      })
      .catch(() => {
        setFeaturedLoading(false);
        addToast?.(t('dashboard.failedToRequestFeatured'), 'error');
      });
  };

  const isSold = product.status === 'sold';
  const featuredStatus = product.featuredRequestStatus || 'none';
  const canRequestFeatured = !isSold && (featuredStatus === 'none' || featuredStatus === 'rejected');
  const isRequested = featuredStatus === 'requested';
  const isApproved = featuredStatus === 'approved';

  return (
    <>
      {/* Featured Request Section */}
      <div className="featuredRequestSection">
        <h4 className="featuredRequestTitle">{t('dashboard.featured')}</h4>
        {canRequestFeatured && (
          <div className="featuredRequestBox">
            <p className="featuredRequestDesc">
              {t('dashboard.makeYourAdStandOut')}
            </p>
            <button
              type="button"
              className="adActionBtn featured"
              onClick={handleRequestFeatured}
              disabled={featuredLoading}
            >
              {featuredLoading ? t('dashboard.requesting') : t('dashboard.requestFeatured')}
            </button>
          </div>
        )}
        {isRequested && (
          <div className="featuredRequestBox featuredRequestBox--pending">
            <span className="featuredRequestIcon">&#9203;</span>
            <div>
              <p className="featuredRequestStatus">{t('dashboard.awaitingApproval')}</p>
              <p className="featuredRequestHint">{t('dashboard.requestBeingReviewed')}</p>
            </div>
          </div>
        )}
        {isApproved && (
          <div className="featuredRequestBox featuredRequestBox--approved">
            <span className="featuredRequestIcon">&#9733;</span>
            <div>
              <p className="featuredRequestStatus">{t('dashboard.yourAdIsFeatured')}</p>
              <p className="featuredRequestHint">{t('dashboard.adGettingExtraVisibility')}</p>
            </div>
          </div>
        )}
        {featuredStatus === 'rejected' && (
          <div className="featuredRequestBox featuredRequestBox--rejected">
            <p className="featuredRequestStatus">{t('dashboard.featuredRequestNotApproved')}</p>
            <p className="featuredRequestHint">{t('dashboard.submitNewRequest')}</p>
          </div>
        )}
      </div>

      <div className="adActions">
        <button
          type="button"
          className="adActionBtn edit"
          onClick={() => history.push(`/ad/${product.id}/edit`)}
        >
          {t('dashboard.editAd')}
        </button>
        {!isSold && (
          <button
            type="button"
            className="adActionBtn sold"
            onClick={() => setShowSoldModal(true)}
          >
            {t('dashboard.markAsSold')}
          </button>
        )}
        <button
          type="button"
          className="adActionBtn delete"
          onClick={() => setShowDeleteModal(true)}
        >
          {t('dashboard.deleteAd')}
        </button>
      </div>
      <ConfirmModal
        isOpen={showDeleteModal}
        title={t('dashboard.deleteAdConfirm')}
        message={t('dashboard.deleteAdMessage')}
        confirmLabel={t('dashboard.deleteAd')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
      <ConfirmModal
        isOpen={showSoldModal}
        title={t('dashboard.markAsSoldConfirm')}
        message={t('dashboard.markAsSoldMessage')}
        confirmLabel={t('dashboard.markAsSold')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleMarkSold}
        onCancel={() => setShowSoldModal(false)}
      />
    </>
  );
}

export default AdActions;
